import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * useWebPush(qrCodeId)
 *
 * Manages Web Push subscription lifecycle for a public tracking page.
 *
 * state values:
 *   - "loading"        initial detection in progress
 *   - "unsupported"    SW or PushManager unavailable in this browser
 *   - "off"            supported but not subscribed
 *   - "blocked"        user denied notification permission
 *   - "subscribed"     actively subscribed to push
 */
export default function useWebPush(qrCodeId) {
  const [state, setState] = useState("loading");
  const [vapidKey, setVapidKey] = useState("");
  const [subEndpoint, setSubEndpoint] = useState(null);
  const [busy, setBusy] = useState(false);

  const supported =
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;

  // Fetch VAPID public key once
  useEffect(() => {
    if (!supported) return;
    api.get("/public/vapid-public-key")
      .then(r => setVapidKey(r.data?.enabled ? r.data.key : ""))
      .catch(() => setVapidKey(""));
  }, [supported]);

  // Detect current state on mount and whenever qrCodeId changes
  useEffect(() => {
    if (!supported) { setState("unsupported"); return; }
    if (Notification.permission === "denied") { setState("blocked"); return; }
    let cancelled = false;

    (async () => {
      try {
        const reg = await navigator.serviceWorker.register("/service-worker.js");
        const existing = await reg.pushManager.getSubscription();
        if (cancelled) return;
        if (existing && Notification.permission === "granted") {
          setSubEndpoint(existing.endpoint);
          setState("subscribed");
        } else {
          setState("off");
        }
      } catch {
        if (!cancelled) setState("off");
      }
    })();

    return () => { cancelled = true; };
  }, [supported, qrCodeId]);

  const enable = useCallback(async () => {
    if (!supported || !qrCodeId) return;
    if (!vapidKey) return;
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        setState(perm === "denied" ? "blocked" : "off");
        return;
      }
      const reg = await navigator.serviceWorker.register("/service-worker.js");
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
      }
      await api.post(`/public/track/${qrCodeId}/subscribe-push`, {
        subscription: sub.toJSON(),
      });
      setSubEndpoint(sub.endpoint);
      setState("subscribed");
    } catch (e) {
      console.error("push enable failed", e);
      setState("off");
    } finally {
      setBusy(false);
    }
  }, [supported, qrCodeId, vapidKey]);

  const disable = useCallback(async () => {
    if (!supported || !qrCodeId) return;
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration("/service-worker.js");
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      const endpoint = sub?.endpoint || subEndpoint;
      if (sub) await sub.unsubscribe();
      if (endpoint) {
        await api.post(`/public/track/${qrCodeId}/unsubscribe-push`, { endpoint });
      }
      setSubEndpoint(null);
      setState("off");
    } catch (e) {
      console.error("push disable failed", e);
    } finally {
      setBusy(false);
    }
  }, [supported, qrCodeId, subEndpoint]);

  return { state, busy, enable, disable, supported, vapidEnabled: !!vapidKey };
}

// Convert a base64url-encoded VAPID public key to a Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}
